import { useContext } from "react";
import {
  DOMAIN,
  PERMISSION,
  useCanManageJobPostsBool,
  useCanViewCareerPlanBool,
  useCanViewCareerPreferencesBool,
  useHasCapabilityBool
} from "@/hooks/useCapabilities";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import { resolveAllowedDefaultTab, sectionGroupKey } from "@/lib/defaultTab";
import { AuthorizationContext } from "@/providers/AuthorizationProvider";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useCanAccessCareerOverview } from "./_hooks/useCanAccessCareerOverview";

export default function CareerIndex() {
  const { pgaId } = useParams();
  const location = useLocation();
  const flagsLoaded = useFlagsLoaded();
  const { loaded: capabilitiesLoaded } = useContext(AuthorizationContext);
  const base = location.pathname.replace(/\/$/, "");
  const group = sectionGroupKey(location.pathname);
  const showOverview = useCanAccessCareerOverview();
  const flags = useFlags();
  const careerTargetsEnabled = flags["pga-career-targets"] === true;
  const canViewPersonCareer = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonCareer
  });
  const canViewCareerPlan = useCanViewCareerPlanBool();
  const canViewCareerPreferences = useCanViewCareerPreferencesBool();
  const canViewCompensationProfiles = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewCompensationProfiles
  });
  const canManageJobPosts = useCanManageJobPostsBool();
  const canViewJobPostings = useHasCapabilityBool({
    domain: DOMAIN.JOBS,
    permission: PERMISSION.viewJobPosts
  });

  if (!pgaId || !flagsLoaded || !capabilitiesLoaded) return null;

  const allowedTabs = [
    showOverview && "overview",
    canViewPersonCareer && "employment",
    canViewPersonCareer && "awards",
    canViewPersonCareer && "governance-roles",
    careerTargetsEnabled && canViewCareerPlan && "career-profile",
    canManageJobPosts && "job-applications",
    canViewCareerPreferences && "job-preferences",
    canViewCompensationProfiles && "compensation",
    canViewJobPostings && "job-postings"
  ].filter((slug): slug is string => Boolean(slug));
  const firstTab = allowedTabs[0];

  if (!firstTab) {
    return <Navigate to={`/people/${pgaId}/person/overview`} replace />;
  }

  const tab =
    flags["backoffice-navigation-preferences"] === true
      ? resolveAllowedDefaultTab(group ?? "", allowedTabs, firstTab)
      : firstTab;

  return <Navigate to={`${base}/${tab}`} replace />;
}
